import { Prescriptions } from "../../services/prescriptions";
import { Appointments } from "../../services/appointments";
import { MedicalCertificates } from "../../services/medical_certificates";
import { Documents } from "../../services/documents";

// Aba da tela de Documentos -> type esperado pelo GET /documents.
const apiTypeFor = filterKey => {
  switch (filterKey) {
    case "receitas":
      return "prescription";
    case "exames":
      return "exam_attachment";
    case "atestados":
      return "medical_certificate";
    default:
      return null;
  }
};

// type devolvido pelo GET /documents -> _type usado nas telas.
const frontTypeFor = apiType => {
  switch (apiType) {
    case "prescription":
      return "receita";
    case "exam_attachment":
      return "exame";
    case "medical_certificate":
      return "atestado";
    default:
      return null;
  }
};

// Converte a resposta do GET /documents ({ total_count, documents }),
// marcando cada documento com o _type usado nas telas.
const withFrontType = res =>
  res.data.documents.map(doc => ({ ...doc, _type: frontTypeFor(doc.type) }));

// Evita que a resposta de uma aba antiga sobrescreva a da aba atual.
let latestPatientDocumentsRequest = 0;

export const state = {
  patientDocuments: [],
  patientDocumentCounts: {},
  recentPatientDocuments: [],
  patientDocumentsTotalCount: 0,
  patientPrescriptions: [],
  patientExamAttachments: [],
  patientMedicalCertificates: [],
  appointmentPrescriptions: [],
  appointmentExamAttachments: [],
  appointmentMedicalCertificates: []
};

export const getters = {
  patientDocuments: state => state.patientDocuments,
  recentPatientDocuments: state => state.recentPatientDocuments,
  patientDocumentsTotalCount: state => state.patientDocumentsTotalCount,
  patientDocumentCounts: state => state.patientDocumentCounts,
  patientPrescriptions: state => state.patientPrescriptions,
  patientExamAttachments: state => state.patientExamAttachments,
  patientMedicalCertificates: state => state.patientMedicalCertificates,
  allPatientDocuments: state =>
    [
      ...state.patientPrescriptions,
      ...state.patientExamAttachments,
      ...state.patientMedicalCertificates
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
  appointmentDocuments: state =>
    [
      ...state.appointmentPrescriptions,
      ...state.appointmentExamAttachments,
      ...state.appointmentMedicalCertificates
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
};

export const mutations = {
  SET_PATIENT_DOCUMENTS(state, data) {
    state.patientDocuments = data;
  },
  SET_PATIENT_DOCUMENT_COUNT(state, { filterKey, count }) {
    state.patientDocumentCounts = {
      ...state.patientDocumentCounts,
      [filterKey]: count
    };
  },
  SET_RECENT_PATIENT_DOCUMENTS(state, { documents, totalCount }) {
    state.recentPatientDocuments = documents;
    state.patientDocumentsTotalCount = totalCount;
  },
  SET_PRESCRIPTIONS(state, data) {
    state.patientPrescriptions = data;
  },
  SET_EXAM_ATTACHMENTS(state, data) {
    state.patientExamAttachments = data;
  },
  SET_MEDICAL_CERTIFICATES(state, data) {
    state.patientMedicalCertificates = data;
  },
  SET_APPOINTMENT_PRESCRIPTIONS(state, data) {
    state.appointmentPrescriptions = data;
  },
  SET_APPOINTMENT_EXAM_ATTACHMENTS(state, data) {
    state.appointmentExamAttachments = data;
  },
  SET_APPOINTMENT_MEDICAL_CERTIFICATES(state, data) {
    state.appointmentMedicalCertificates = data;
  }
};

export const actions = {
  postPrescription({}, payload) {
    return Prescriptions.postPrescription(payload);
  },

  // Tela de Documentos: uma página da aba. O total_count da resposta vira o
  // contador daquela aba (as outras só ganham contador ao serem carregadas).
  async fetchPatientDocuments({ commit }, { filterKey = "todos", page = 1 }) {
    const requestId = ++latestPatientDocumentsRequest;
    const type = apiTypeFor(filterKey);
    const res = await Documents.fetchPatientDocuments(type, page);
    const documents = withFrontType(res);

    commit("SET_PATIENT_DOCUMENT_COUNT", {
      filterKey,
      count: res.data.total_count
    });
    if (requestId === latestPatientDocumentsRequest) {
      commit("SET_PATIENT_DOCUMENTS", documents);
    }
  },

  // Dashboard: só a página 1 ("Documentos recentes") e o total do back.
  async fetchRecentPatientDocuments({ commit }) {
    const res = await Documents.fetchPatientDocuments(null, 1);
    commit("SET_RECENT_PATIENT_DOCUMENTS", {
      documents: withFrontType(res),
      totalCount: res.data.total_count
    });
  },

  async fetchPatientPrescriptions({ commit }) {
    const res = await Prescriptions.fetchPatientPrescriptions();
    commit(
      "SET_PRESCRIPTIONS",
      res.data.map(p => ({ ...p, _type: "receita" }))
    );
  },

  async fetchPatientExamAttachments({ commit }) {
    const res = await Appointments.fetchPatientExamAttachments();
    commit(
      "SET_EXAM_ATTACHMENTS",
      res.data.map(e => ({ ...e, _type: "exame" }))
    );
  },

  async fetchPatientMedicalCertificates({ commit }) {
    const res = await MedicalCertificates.fetchPatientMedicalCertificates();
    commit(
      "SET_MEDICAL_CERTIFICATES",
      res.data.map(c => ({ ...c, _type: "atestado" }))
    );
  },

  async fetchAppointmentPrescriptions({ commit }, appointmentId) {
    const res = await Prescriptions.fetchDoctorPrescriptions(appointmentId);
    commit(
      "SET_APPOINTMENT_PRESCRIPTIONS",
      res.data.map(p => ({ ...p, _type: "receita" }))
    );
  },

  async fetchAppointmentExamAttachments({ commit }, appointmentId) {
    const res = await Appointments.listAdminExamAttachments(appointmentId);
    commit(
      "SET_APPOINTMENT_EXAM_ATTACHMENTS",
      res.data.map(e => ({ ...e, _type: "exame" }))
    );
  },

  async fetchAppointmentMedicalCertificates({ commit }, appointmentId) {
    const res = await MedicalCertificates.listAppointmentMedicalCertificates(
      appointmentId
    );
    commit(
      "SET_APPOINTMENT_MEDICAL_CERTIFICATES",
      res.data.map(c => ({ ...c, _type: "atestado" }))
    );
  },

  async fetchAppointmentPrescriptionsForPatient({ commit }, appointmentId) {
    const res = await Prescriptions.fetchPatientPrescriptions();
    commit(
      "SET_APPOINTMENT_PRESCRIPTIONS",
      res.data
        .filter(p => p.appointment_id === appointmentId)
        .map(p => ({ ...p, _type: "receita" }))
    );
  },

  async fetchAppointmentExamAttachmentsForPatient({ commit }, appointmentId) {
    const res = await Appointments.listExamAttachments(appointmentId);
    commit(
      "SET_APPOINTMENT_EXAM_ATTACHMENTS",
      res.data.map(e => ({ ...e, _type: "exame" }))
    );
  },

  async fetchAppointmentMedicalCertificatesForPatient(
    { commit },
    appointmentId
  ) {
    const res = await MedicalCertificates.fetchPatientMedicalCertificates();
    commit(
      "SET_APPOINTMENT_MEDICAL_CERTIFICATES",
      res.data
        .filter(c => c.appointment_id === appointmentId)
        .map(c => ({ ...c, _type: "atestado" }))
    );
  }
};
